import useAutocomplete, { AutocompleteGetTagProps } from '@mui/material/useAutocomplete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, styled } from '@mui/material/styles';
import { autocompleteClasses } from '@mui/material/Autocomplete';

type Option = {
  label: string;
  value: string;
};

type Props = {
  options: Option[];
  id: string;
  label?: string;
  onChange: (values: string[]) => void;
  value: Option[];
};

export const TagSelect: React.FC<Props> = ({ options, label, id, onChange, value }) => {
  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    focused,
    setAnchorEl,
  } = useAutocomplete({
    id,
    multiple: true,
    onChange: (_event, values) => {
      onChange(values.map((v) => v.value));
    },
    options,
    getOptionLabel: (option) => option.label,
    isOptionEqualToValue: (option, v) => option.value === v.value,
    value,
  });

  return (
    <Root>
      <div {...getRootProps()}>
        <Label {...getInputLabelProps()}>{label}</Label>
        <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''}>
          {value.map((option: Option, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });

            return <StyledTag key={key} {...tagProps} label={option.label} />;
          })}
          <input {...getInputProps()} />
        </InputWrapper>
      </div>
      {groupedOptions.length > 0 ? (
        <Listbox {...getListboxProps()}>
          {groupedOptions.map((option, index) => {
            const { key, ...optionProps } = getOptionProps({ option: option as Option, index });

            return (
              <li key={key} {...optionProps}>
                <span>{(option as Option).label}</span>
                <CheckIcon fontSize="small" />
              </li>
            );
          })}
        </Listbox>
      ) : null}
    </Root>
  );
};

type TagProps = ReturnType<AutocompleteGetTagProps> & {
  label: string;
};

const Tag: React.FC<TagProps> = ({ label, onDelete, ...props }) => (
  <div {...props}>
    <span>{label}</span>
    <CloseIcon onClick={onDelete} />
  </div>
);

const Root = styled('div')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: theme.fontSizes.sm,
}));

const Label = styled('label')`
  padding: 0 0 4px;
  line-height: 1.5;
  display: block;
`;

const InputWrapper = styled('div')(({ theme }) => ({
  width: '300px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  borderRadius: '4px',
  padding: '1px',
  display: 'flex',
  flexWrap: 'wrap',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  '&.focused': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  '& input': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    height: '30px',
    boxSizing: 'border-box',
    padding: '4px 6px',
    width: '0',
    minWidth: '30px',
    flexGrow: 1,
    border: 0,
    margin: 0,
    outline: 0,
  },
}));

const StyledTag = styled(Tag)<TagProps>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: '24px',
  margin: '2px',
  lineHeight: '22px',
  backgroundColor: theme.palette.neutral.subtle,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '2px',
  boxSizing: 'content-box',
  padding: '0 4px 0 10px',
  outline: 0,
  overflow: 'hidden',
  '&:focus': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.success.light,
  },
  '& span': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  '& svg': {
    fontSize: '12px',
    cursor: 'pointer',
    padding: '4px',
  },
}));

const Listbox = styled('ul')(({ theme }) => ({
  width: '300px',
  margin: '2px 0 0',
  padding: 0,
  position: 'absolute',
  listStyle: 'none',
  backgroundColor: theme.palette.background.paper,
  overflow: 'auto',
  maxHeight: '250px',
  borderRadius: '4px',
  boxShadow: theme.shadows[3],
  zIndex: 1,
  '& li': {
    padding: '5px 12px',
    display: 'flex',
    '& span': {
      flexGrow: 1,
    },
    '& svg': {
      color: 'transparent',
    },
  },
  "& li[aria-selected='true']": {
    backgroundColor: theme.palette.neutral.subtle,
    fontWeight: 600,
    '& svg': {
      color: theme.palette.primary.main,
    },
  },
  [`& li.${autocompleteClasses.focused}`]: {
    backgroundColor: theme.palette.action.hover,
    cursor: 'pointer',
    '& svg': {
      color: 'currentColor',
    },
  },
}));
